import { useState } from 'react';
import { useT } from '@hana/i18n';
import { IconContainer, IconFileText, IconPlus, IconSparkles, IconUpload, IconPencil, IconInfoCircle, IconEye, IconChevronRight } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import { EDIT_CONTS, EDIT_DOCS, EDIT_FEES, EDIT_FT, EDIT_TERM, EDIT_VGM, EDIT_VOL, isoCheck, type EditCont } from './editSample';
import s from './blEdit.module.css';

// Mint-BLEdit › phần mở của Hàng & container (Container & VGM), Điều khoản & cước (Phí & chi phí),
// Ghi chú / clause (free time DEM / DET + dòng in) và Chứng từ B/L. Chỉ đọc: nút ghi báo toast.

const fmtN = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const Hint = ({ tip }: { tip: string }) => (
  <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
);
const SubHead = ({ children, tip, right }: { children: string; tip?: string; right?: React.ReactNode }) => (
  <div className={s.sub}>
    <h3 className={s.h3}>{children}</h3>
    {tip && <Hint tip={tip} />}
    <span className={s.grow} />
    {right}
  </div>
);

const COLS: readonly (readonly [string, string, boolean?])[] = [['Số container', '23%'], ['Seal', '18%'], ['Loại', '8%'], ['Kiện', '12%', true], ['GW (kg)', '11%', true], ['CBM', '8%', true], ['VGM (kg)', '12%', true], ['', '8%']];

export function ContBlock({ ro, unit }: { ro: boolean; unit: string }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [rows, setRows] = useState<EditCont[]>(() => EDIT_CONTS.map((c) => ({ ...c })));
  const upd = (i: number, k: 'no' | 'seal', v: string) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v.toUpperCase() } : x)));
  const sumGw = rows.reduce((a, c) => a + c.gw, 0);
  const sumPk = rows.reduce((a, c) => a + c.pk, 0);
  const sumCbm = rows.reduce((a, c) => a + c.cbm, 0);
  const mismatch = rows.length !== EDIT_VOL;
  const vgmOk = EDIT_VGM === 'Submitted';
  return (
    <div className={s.subBlock}>
      <div className={s.sub}>
        <span className={s.ico} aria-hidden="true"><IconContainer size={18} stroke={1.8} /></span>
        <h3 className={s.h3}>Container &amp; VGM</h3>
        <span className={s.subSum}>{`${rows.length} cont · ${fmtN(sumPk)} ${unit} · ${fmtN(sumGw)} KGS · ${fmtN(sumCbm)} CBM`}</span>
        <span className={s.grow} />
        <span className={`${s.vgm} ${vgmOk ? s.vgmOk : s.vgmWait}`} title={vgmOk ? 'Đã nộp VGM cho hãng' : mismatch ? 'Đủ container mới nộp VGM' : 'Nộp VGM cho hãng'}><i />{`VGM ${EDIT_VGM}`}</span>
      </div>
      <div className={s.acts0}>
        <button type="button" className={`${s.btn} ${s.btnP}`} disabled={ro} onClick={noWrite}><IconSparkles size={15} stroke={1.9} aria-hidden="true" />{t('bl.askHana')}</button>
        <button type="button" className={s.btn} disabled={ro} onClick={noWrite}><IconUpload size={16} stroke={1.8} aria-hidden="true" />{t('bl.importXls')}</button>
        <button type="button" className={s.btn} disabled={ro || vgmOk || mismatch} onClick={noWrite}><IconUpload size={16} stroke={1.8} aria-hidden="true" />{t('bl.submitVgm')}</button>
      </div>
      <table id="bl-cont" className={s.ct}>
        <thead>
          <tr>{COLS.map(([k, w, r]) => <th key={k || 'x'} style={{ width: w, textAlign: r ? 'right' : 'left' }}>{k}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((c, i) => {
            const ck = isoCheck(c.no);
            const sealMiss = c.seal.trim() === '';
            const sealDirty = !!EDIT_CONTS[i] && EDIT_CONTS[i]!.seal !== c.seal;
            return (
              <tr key={i}>
                <td><input id={`bl-c-no-${i + 1}`} aria-label={`Số container dòng ${i + 1}`} title={ck.k} className={`${s.fin} ${s.cin} ${ck.ok ? '' : s.finErr}`} readOnly={ro} value={c.no} onChange={(e) => upd(i, 'no', e.target.value)} /></td>
                <td><input id={`bl-c-seal-${i + 1}`} aria-label={`Seal dòng ${i + 1}`} className={`${s.fin} ${s.cin} ${sealMiss ? s.finErr : sealDirty ? s.finDirty : ''}`} readOnly={ro} value={c.seal} onChange={(e) => upd(i, 'seal', e.target.value)} /></td>
                <td>{c.type}</td>
                <td className={s.num}>{`${c.pk} ${unit}`}</td>
                <td className={s.num}>{fmtN(c.gw)}</td>
                <td className={s.num}>{fmtN(c.cbm)}</td>
                <td className={`${s.num} ${s.numB}`}>{fmtN(c.gw + c.tare)}</td>
                <td className={s.num}><button type="button" className={s.x} aria-label={t('bledit.delRowN', { n: i + 1 })} title={t('bledit.delRow')} disabled={ro} onClick={() => setRows((r) => r.filter((_, j) => j !== i))}>×</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={s.contFoot}>
        <button type="button" className={s.add} disabled={ro} onClick={() => setRows((r) => r.concat([{ no: '', seal: '', type: '40HC', pk: 0, gw: 0, cbm: 0, tare: 0 }]))}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('bl.addCont')}</button>
        <span className={mismatch ? s.footErr : s.footOk}>{mismatch ? `Booking ${EDIT_VOL}×40HC · đang có ${rows.length} cont` : `Khớp ${EDIT_VOL}×40HC · VGM = GW + tare`}</span>
      </div>
    </div>
  );
}

export function FeesBlock({ ro }: { ro: boolean }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  return (
    <div className={s.subBlock}>
      <SubHead tip="Phí thu khách và chi phí nội bộ gắn B/L. Số tiền chỉ xem ở màn tài chính (Chi phí)." right={<span className={s.subSum}>{`${EDIT_FEES.length} dòng · số tiền xem ở Chi phí`}</span>}>{t('bl.feesTitle')}</SubHead>
      {EDIT_FEES.map((f) => (
        <div key={f.k} className={s.line}>
          <span className={s.side}>{f.side}</span>
          <span className={s.lk} title={f.k}>{f.k}</span>
          <span className={`${s.rs} ${f.wait ? s.waitC : ''}`}>{f.st}</span>
          <button type="button" className={s.btnSm} disabled={ro} aria-label={`Sửa dòng phí ${f.k}`} onClick={noWrite}><IconPencil size={14} stroke={1.8} aria-hidden="true" />{t('bl.edit')}</button>
        </div>
      ))}
      <div className={s.acts}>
        <button type="button" className={s.add} disabled={ro} onClick={noWrite}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('bl.svcFees')}</button>
        <button type="button" className={s.lnk} onClick={noWrite}>{t('bl.payReq')}<IconChevronRight size={14} stroke={1.9} aria-hidden="true" /></button>
      </div>
    </div>
  );
}

export function ClauseBody({ ro }: { ro: boolean }) {
  const t = useT();
  const [mode, setMode] = useState<'split' | 'dnd'>('split');
  const [ft, setFt] = useState<Record<string, string>>({ ...EDIT_FT });
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [print, setPrint] = useState(true);
  const set = (k: string) => (v: string) => { setFt((o) => ({ ...o, [k]: v.replace(/[^0-9]/g, '') })); setDirty((o) => ({ ...o, [k]: true })); };
  const anyDirty = Object.keys(dirty).length > 0;
  const fixed = { k: 'Áp cho', v: `${EDIT_VOL} cont 40HC`, hint: 'Tất cả cont của B/L' };
  const fields: readonly { k: string; key?: string; hint: string; v?: string; locked?: boolean }[] = mode === 'split'
    ? [{ k: 'DEM (ngày)', key: 'dem', hint: 'Lưu bãi cảng, tính từ ngày dỡ' }, { k: 'DET (ngày)', key: 'det', hint: 'Giữ vỏ, tính từ ngày lấy cont' }, { k: 'Plug (ngày)', key: 'plug', hint: 'Chỉ hàng lạnh · lô này hàng khô', locked: true }, { ...fixed, locked: true }]
    : [{ k: 'DnD gộp (ngày)', key: 'dnd', hint: 'DEM + DET tính chung' }, { k: 'Plug (ngày)', key: 'plug', hint: 'Chỉ hàng lạnh · lô này hàng khô', locked: true }, { ...fixed, locked: true }, { k: 'Nguồn', v: 'Booking', hint: 'Theo booking confirmation', locked: true }];
  const line = mode === 'split' ? `${ft.dem || '0'} DAYS FREE DEMURRAGE / ${ft.det || '0'} DAYS FREE DETENTION AT POD` : `${ft.dnd || '0'} DAYS COMBINED FREE TIME (DEM/DET) AT POD`;
  const lines = ["SHIPPER'S LOAD, COUNT & SEAL", EDIT_TERM, ...(print ? [line] : [])];
  const src = `Theo booking NAMSUNG · ${anyDirty ? 'đã sửa' : 'chưa sửa'}`;
  return (
    <div className={s.body}>
      <div className={s.subRow}>
        <h3 className={s.h3}>{t('bl.freeTime')}</h3>
        <Hint tip={src} />
        <span className={s.grow} />
        <div role="radiogroup" aria-label="Kiểu free time" className={s.fseg}>
          {([['split', 'DEM / DET riêng'], ['dnd', 'Gộp DnD']] as const).map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={mode === k} disabled={ro} className={`${s.fsegB} ${s.fsegS}`} onClick={() => setMode(k)}>{l}</button>
          ))}
        </div>
      </div>
      <div className={s.fgrid}>
        {fields.map((f) => {
          const ronly = ro || !!f.locked;
          const val = f.key ? ft[f.key] ?? '' : f.v ?? '';
          return (
            <label key={f.k} className={s.fld}>
              <div className={s.fk}><span>{f.k}</span></div>
              <input aria-label={f.k} title={f.hint} inputMode="numeric" className={`${s.fin} ${f.key && dirty[f.key] ? s.finDirty : ''}`} readOnly={ronly} value={val} onChange={(e) => f.key && set(f.key)(e.target.value)} />
              <div className={s.hint}>{f.hint}</div>
            </label>
          );
        })}
      </div>
      <SubHead tip="Các dòng clause in dưới mô tả hàng. Dòng free time in theo lựa chọn bên dưới.">{t('bl.printLine')}</SubHead>
      <div className={s.clauses}>{lines.map((c) => <div key={c}>{c}</div>)}</div>
      <label className={s.chk}><input type="checkbox" checked={print} disabled={ro} onChange={() => setPrint(!print)} />{t('bl.printFree')}</label>
    </div>
  );
}

export function DocsBody({ ro }: { ro: boolean }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  return (
    <div className={s.body}>
      {EDIT_DOCS.map(([k, sub]) => (
        <div key={k} className={s.line}>
          <span className={s.ico} aria-hidden="true"><IconFileText size={18} stroke={1.8} /></span>
          <span className={s.lk} title={k}>{k}</span>
          <span className={s.rs}>{sub}</span>
          <button type="button" className={s.btnSm} onClick={() => toast({ msg: `Mở ${k} trong trình xem tệp` })}><IconEye size={14} stroke={1.8} aria-hidden="true" />Xem</button>
        </div>
      ))}
      <div className={s.acts}>
        <button type="button" className={s.btn} disabled={ro} onClick={noWrite}><IconUpload size={16} stroke={1.8} aria-hidden="true" />{t('bl.uploadFinal')}</button>
        <button type="button" className={s.lnk} disabled={ro} onClick={noWrite}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('bl.otherDocs')}</button>
      </div>
    </div>
  );
}
